import { useMemo } from 'react'
import { useConfig, useTokenManagers } from '@/api/queries'
import type { TokenAssignment } from '@/api/types'
import { LinkButton } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/EmptyState'
import { Icons } from '@/components/ui/Icons'
import { Widget } from '@/components/ui/Widget'
import { dateTime, toDate } from '@/lib/format'
import styles from './TokenWidget.module.css'

interface TeamToken {
  team: string
  current: TokenAssignment | undefined
  next: TokenAssignment | undefined
}

/**
 * The only teams shown, in this order, with the names the database may store them under.
 * The rota still records the platform team as "Spider"; names match case-insensitively, with or without " Team".
 */
const SHOWN_TEAMS = [
  { label: 'Platform Team', names: ['platform', 'spider'] },
  { label: 'Scraping Team', names: ['scraping'] },
]

const teamKey = (name: string) => name.trim().toLowerCase().replace(/\s+team$/, '')

/**
 * Who holds the support token per shown team: the latest assignment that already started, and the next holder.
 * A shown team with no assignment still gets a card, so a gap in the rota is visible.
 */
function groupByTeam(assignments: TokenAssignment[]): TeamToken[] {
  const now = Date.now()
  return SHOWN_TEAMS.map(({ label, names }) => {
    const sorted = assignments
      .filter((a) => names.includes(teamKey(a.team)))
      .sort((a, b) => toDate(a.date).getTime() - toDate(b.date).getTime())
    const started = sorted.filter((a) => toDate(a.date).getTime() <= now)
    // One row per working day: "since" is the first day of the current holder's unbroken run.
    let first = started.length - 1
    while (first > 0 && started[first - 1].member === started[first].member) first--
    const current = started[first]
    // The next holder is the first upcoming row for someone else.
    const next = sorted.find((a) => toDate(a.date).getTime() > now && a.member !== current?.member)
    return { team: label, current, next }
  })
}

export function TokenWidget({ className }: { className?: string }) {
  const { data, isLoading, error } = useTokenManagers()
  const { data: config } = useConfig()
  const teams = useMemo(() => groupByTeam(data ?? []), [data])

  return (
    <Widget
      title="Token managers"
      loading={isLoading}
      className={className}
      actions={
        config?.links.tokenWiki ? (
          <LinkButton size="sm" href={config.links.tokenWiki} icon={<Icons.External size={12} />}>
            Wiki
          </LinkButton>
        ) : undefined
      }
    >
      {error ? (
        <ErrorState error={error} />
      ) : (
        <ul className={styles.list}>
          {teams.map((t) => (
            <li key={t.team}>
              <div className={styles.teamName}>{t.team}</div>
              <div className={styles.holder}>{t.current?.member ?? <span className="u-faint">unassigned</span>}</div>
              <div className={styles.meta}>
                {t.current && <span>since {dateTime(t.current.date, 'd MMM')}</span>}
                {t.next && (
                  <span>
                    {t.current ? ' · ' : ''}next {t.next.member} on {dateTime(t.next.date, 'd MMM')}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Widget>
  )
}
